import React from 'react';
import HTMLRenderer from 'react-html-renderer';
import cn from 'classnames';

import MagicLink from '../link';
import { Imgix, CloudinaryFluid, camelize } from '../../utils';

import { Wrapper } from './Html.style';

const imgResponsive = {
  xxs: 10,
};

const style2obj = (str) =>
  `${str}`.split(';').reduce((res, line) => {
    const parts = line.trim().split(':');
    if (parts.length !== 2) {
      return res;
    }
    const key = camelize(`${parts[0]}`.trim());
    res[key] = `${parts[1]}`.trim();
    return res;
  }, {});

const processImage = (src, { width, height }, props, maxWidth) => {
  let url = src;

  if (src.indexOf('prismic-io.s3.amazonaws.com') !== -1) {
    url = src.replace('prismic-io.s3.amazonaws.com', 'images.prismic.io');
    url = `${url}?auto=compress%2Cformat`;
  }

  if (src.indexOf('https://cdn.accentuate.cloud/') === 0) {
    url = `https://images.accentuate.io/?image=${encodeURIComponent(
      src
    )}&c_options=`;

    const style = (props.style || '').split(';').reduce((acc, s) => {
      const parts = s.split(':').map((s) => `${s}`.trim());
      if (parts.length < 2) {
        return acc;
      }
      const key = parts[0];
      let value = parts[1];

      if (value.indexOf('px') !== -1 && value.indexOf('calc') === -1) {
        value = parseInt(value.replace('px', ''), 10);
      }

      acc[key] = value;
      return acc;
    }, {});

    const infos = CloudinaryFluid(
      {
        cloudinary_src: url,
        aspect_ratio: 1,
      },
      { maxWidth: style.width || maxWidth }
    );

    const newProps = {
      ...props,
      ...infos,
      style: undefined,
    };
    delete newProps.aspectRatio;
    delete newProps.class;
    delete newProps.children;
    delete newProps.srcSetWebp;

    return (
      <picture className={props.class}>
        <source
          type="image/webp"
          srcSet={infos.srcSetWebp}
          sizes={infos.sizes}
        />
        <img alt="Ipone" loading="lazy" {...newProps} />
      </picture>
    );
  }

  return (
    <Imgix
      sizes={imgResponsive}
      {...props}
      url={url}
      dimensions={{
        width,
        height,
      }}
      maxWidth={maxWidth}
    />
  );
};

const resetStyles = ({ name, style, children, ...props }) => {
  const newProps = {
    ...props,
  };

  newProps.className = props.class;
  delete newProps.class;

  return React.createElement(
    name,
    {
      ...newProps,
      style: {
        ...style2obj(style),
        fontFamily: undefined,
        backgroundColor: undefined,
        color: undefined,
        fontSize: undefined,
      },
    },
    children
  );
};

const format = (maxWidth) => ({
  a: ({ name, style, ...props }) => (
    <MagicLink
      {...props}
      className={cn(props.class, 'simple-link red noicon')}
    />
  ),
  img: ({ name, src, ...props }) => {
    const width = parseInt(props['data-width'] || 1);
    const height = parseInt(props['data-height'] || 1);
    return processImage(
      src,
      {
        width,
        height,
      },
      props,
      maxWidth
    );
  },
  p: ({ ...props }) => {
    // remove empty p with only br
    if (
      !Array.isArray(props.children) &&
      typeof props.children !== 'string' &&
      props.children?.type === 'br'
    ) {
      return null;
    }
    return resetStyles(props);
  },
  iframe: ({ ...props }, children) => {
    return <iframe title="frame" {...props} width="100%" height="400px" />;
  },
  span: resetStyles,
});

const Html = ({ html, maxWidth = 1400, ...props }) => {
  return (
    <Wrapper {...props} style={{ maxWidth }}>
      <HTMLRenderer html={html} components={format(maxWidth)} />
    </Wrapper>
  );
};

export default React.memo(Html);
